import React, { useState, useEffect } from 'react';
import { Typography, Alert, Card, List, Space, Tooltip, Spin, Button, Collapse, Tag } from 'antd';
import { 
  CheckCircleFilled, 
  ExclamationCircleFilled, 
  CloseCircleFilled, 
  InfoCircleOutlined, 
  ReloadOutlined,
  CaretRightOutlined
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const RequirementItem = ({ item }) => {
  let icon;
  let color;
  
  switch (item.status) {
    case 'success':
      icon = <CheckCircleFilled style={{ color: '#52c41a' }} />;
      color = '#52c41a';
      break;
    case 'warning':
      icon = <ExclamationCircleFilled style={{ color: '#faad14' }} />;
      color = '#faad14';
      break;
    case 'error':
      icon = <CloseCircleFilled style={{ color: '#f5222d' }} />;
      color = '#f5222d';
      break;
    default:
      icon = <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      color = '#1890ff';
  }

  const getAlert = () => {
    if (item.status === 'success') return null;
    
    let message, description;
    
    // Define alerts based on requirement type
    if (item.title === 'Max Execution Time') {
      message = "Low Execution Time Limit Detected";
      description = (
        <div>
          <Paragraph>
            <strong>What this means:</strong> PHP's max execution time limits how long your workflows can run. Complex AI workflows, especially those processing large documents or running multiple AI models in sequence, need more time.
          </Paragraph>
          <Paragraph>
            <strong>Potential issues:</strong> With insufficient execution time, your workflows might:
          </Paragraph>
          <ul>
            <li>Terminate unexpectedly in the middle of processing</li>
            <li>Fail when handling large data sets or documents</li>
            <li>Generate incomplete results</li>
            <li>Show unexpected errors during complex operations</li>
          </ul>
          <Paragraph>
            <strong>How to fix:</strong> You can increase the execution time in several ways:
          </Paragraph>
          <ol>
            <li>Add to your php.ini file: <code>max_execution_time = 600</code></li>
            <li>Add to your .htaccess file: <code>php_value max_execution_time 600</code></li>
            <li>Contact your hosting provider to increase the limit</li>
          </ol>
          <Paragraph>
            For shared hosting, you might need to upgrade to a plan with more resources or switch to a host that allows higher limits.
          </Paragraph>
          <Paragraph>
            Note: Basic workflows will still function with the current setting, but you may encounter limitations with more complex operations.
          </Paragraph>
        </div>
      );
    } else if (item.title === 'Memory Limit') {
      message = "Insufficient Memory Limit Detected";
      description = (
        <div>
          <Paragraph>
            <strong>What this means:</strong> PHP memory limit controls how much memory your workflows can use. AI operations like processing images, large text datasets, or running multiple AI services require adequate memory.
          </Paragraph>
          <Paragraph>
            <strong>Potential issues:</strong> With limited memory, you may experience:
          </Paragraph>
          <ul>
            <li>Fatal PHP errors ("Allowed memory size exhausted")</li>
            <li>Incomplete processing of large documents or datasets</li>
            <li>AI model failures during complex operations</li>
            <li>Degraded performance or timeouts</li>
          </ul>
          <Paragraph>
            <strong>How to fix:</strong> Increase your PHP memory limit using one of these methods:
          </Paragraph>
          <ol>
            <li>Add to your php.ini file: <code>memory_limit = 512M</code> (or higher)</li>
            <li>Add to your .htaccess file: <code>php_value memory_limit 512M</code></li>
            <li>Add to wp-config.php: <code>define('WP_MEMORY_LIMIT', '512M');</code></li>
          </ol>
          <Paragraph>
            If you can't modify these settings, you may need to contact your hosting provider or upgrade to a hosting plan with more resources.
          </Paragraph>
        </div>
      );
    } else if (item.title.includes('MySQL') || item.title.includes('MariaDB')) {
      message = "Database Version Issue Detected";
      description = (
        <div>
          <Paragraph>
            <strong>What this means:</strong> AI Workflow Automation uses advanced database features only available in newer database versions. Your current version may limit functionality.
          </Paragraph>
          <Paragraph>
            <strong>Potential issues:</strong> With an outdated database, you may experience:
          </Paragraph>
          <ul>
            <li>Issues with cost management and analytics features</li>
            <li>Slower performance, especially with large workflows</li>
            <li>Potential database errors during complex operations</li>
            <li>Limitations with concurrent workflow executions</li>
          </ul>
          <Paragraph>
            <strong>How to fix:</strong> Database upgrades must typically be performed by your hosting provider:
          </Paragraph>
          <ol>
            <li>Contact your hosting provider and request an upgrade to MySQL 8.0+ or MariaDB 10.5+</li>
            <li>Consider migrating to a hosting provider that offers current database versions</li>
            <li>If using a local development environment, update your database software</li>
          </ol>
          <Paragraph>
            Always back up your database before any version upgrade.
          </Paragraph>
        </div>
      );
    } else if (item.title === 'WordPress Version') {
      message = "WordPress Version Too Old";
      description = (
        <div>
          <Paragraph>
            <strong>What this means:</strong> AI Workflow Automation requires WordPress 6.0+ to access necessary core functions and ensure security.
          </Paragraph>
          <Paragraph>
            <strong>Potential issues:</strong> With an outdated WordPress version:
          </Paragraph>
          <ul>
            <li>Critical plugin functions will not work properly</li>
            <li>REST API features may be limited or unavailable</li>
            <li>Security vulnerabilities may be present</li>
            <li>Other compatibility issues with modern plugins</li>
          </ul>
          <Paragraph>
            <strong>How to fix:</strong> Update your WordPress installation:
          </Paragraph>
          <ol>
            <li>Back up your website completely (files and database)</li>
            <li>Go to Dashboard → Updates and perform the update</li>
            <li>If automatic updates don't work, consider a manual update</li>
          </ol>
          <Paragraph>
            If you're on a very old version, consider performing incremental updates rather than jumping directly to the latest version.
          </Paragraph>
        </div>
      );
    } else if (item.title === 'PHP Version') {
      message = "PHP Version Too Old";
      description = (
        <div>
          <Paragraph>
            <strong>What this means:</strong> AI Workflow Automation requires PHP 8.0+ to use modern language features for performance and security.
          </Paragraph>
          <Paragraph>
            <strong>Potential issues:</strong> With an outdated PHP version:
          </Paragraph>
          <ul>
            <li>The plugin will not function correctly or may not activate at all</li>
            <li>You'll miss critical performance improvements in newer PHP versions</li>
            <li>Security vulnerabilities present in older PHP versions may put your site at risk</li>
            <li>Advanced features like asynchronous processing will be limited</li>
          </ul>
          <Paragraph>
            <strong>How to fix:</strong> Update your PHP version:
          </Paragraph>
          <ol>
            <li>Contact your hosting provider to request an upgrade to PHP 8.0+</li>
            <li>If you manage your own server, follow your operating system's guidelines for updating PHP</li>
            <li>After upgrading, check your site thoroughly as some older plugins may need updates</li>
          </ol>
          <Paragraph>
            Many hosting providers offer a simple PHP version selector in their control panel.
          </Paragraph>
        </div>
      );
    } else if (item.title === 'REST API Configuration' && item.status !== 'success') {
      message = "REST API Issues Detected";
      
      // Create a more detailed description based on the specific issue
      let specificRecommendations = [];
      if (item.details && 
          item.details.recommendations && 
          Array.isArray(item.details.recommendations)) {
        specificRecommendations = item.details.recommendations.map(rec => (
          <div key={rec.title || 'recommendation'} style={{ marginBottom: 12 }}>
            <Paragraph>
              <strong>{rec.title}</strong>
              {rec.priority === 'high' && (
                <Tag color="red" style={{ marginLeft: 8 }}>Critical</Tag>
              )}
              {rec.priority === 'medium' && (
                <Tag color="orange" style={{ marginLeft: 8 }}>Important</Tag>
              )}
            </Paragraph>
            <Paragraph>{rec.description}</Paragraph>
          </div>
        ));
      }
      
      description = (
        <div>
          <Paragraph>
            <strong>What this means:</strong> AI Workflow Automation relies heavily on the WordPress REST API for all its operations, including running workflows and communicating with AI services.
          </Paragraph>
          
          {item.details && item.details.specific_issue && (
            <Alert
              message="Detected Issue"
              description={item.message}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Paragraph>
            <strong>Potential issues:</strong> With REST API problems:
          </Paragraph>
          <ul>
            <li>Workflows will fail to execute</li>
            <li>The workflow builder interface may not work correctly</li>
            <li>API calls to external services will fail</li>
            <li>Chat features and assistants won't function</li>
          </ul>
          
          <Paragraph>
            <strong>Recommended fixes:</strong>
          </Paragraph>
          
          {specificRecommendations.length > 0 ? (
            specificRecommendations
          ) : (
            <ol>
              <li>Go to Settings → Permalinks and select any option other than "Plain" (Post name is recommended)</li>
              <li>Check security plugins that might be blocking REST API access</li>
              <li>Verify your .htaccess file is correctly configured and WordPress can modify it</li>
              <li>Ensure your web server configuration allows REST API requests</li>
            </ol>
          )}
          
          <Paragraph>
            If using security plugins like Wordfence, Shield, or iThemes Security, check their settings for REST API restrictions.
          </Paragraph>
        </div>
      );
    } else {
      // Generic fallback
      return null;
    }

    return (
      <Collapse
        style={{ marginTop: 8, marginBottom: 16 }}
        bordered={false}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        <Panel 
          header={<Text strong style={{ color: item.status === 'warning' ? '#faad14' : '#f5222d' }}>{message}</Text>} 
          key="1"
        >
          {description}
        </Panel>
      </Collapse>
    );
  };

  return (
    <div>
      <List.Item>
        <Space>
          {icon}
          <span>{item.title}:</span>
          <Text strong style={{ color }}>{item.value}</Text>
          {item.required && <Text type="secondary">(Required: {item.required})</Text>}
        </Space>
        {item.message && (
          <Tooltip title={item.message}>
            <InfoCircleOutlined style={{ color: '#bfbfbf', marginLeft: 8 }} />
          </Tooltip>
        )}
      </List.Item>
      {getAlert()}
    </div>
  );
};

const SystemRequirements = () => {
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState(null);
  const [error, setError] = useState(null);

  const fetchSystemRequirements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/system-requirements`, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      setRequirements(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching system requirements:', err);
      setError('Failed to fetch system requirements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemRequirements();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button 
            icon={<ReloadOutlined />} 
            size="small" 
            onClick={fetchSystemRequirements}
          >
            Retry
          </Button>
        }
      />
    );
  }

  if (!requirements) {
    return null;
  }

  const getOverallStatus = () => {
    if (requirements.items.some(item => item.status === 'error')) {
      return 'error';
    }
    if (requirements.items.some(item => item.status === 'warning')) {
      return 'warning';
    }
    return 'success';
  };

  const overallStatus = getOverallStatus();
  const statusMessages = {
    success: 'Your system meets all requirements for AI Workflow Automation.',
    warning: 'Your system meets minimum requirements, but some optimizations are recommended.',
    error: 'Your system does not meet some critical requirements. The plugin may not function properly.'
  };

  const renderCronSection = () => {
    if (!requirements.cron) return null;
    
    const cronAlerts = [];
    
    // Cron disabled alert
    if (!requirements.cron.enabled) {
      cronAlerts.push(
        <Alert
          key="cron-disabled"
          message="WordPress Cron is Disabled"
          description={
            <div>
              <Paragraph>
                Many features of AI Workflow Automation rely on WordPress cron to function properly, including:
              </Paragraph>
              <ul>
                <li>Scheduled workflows execution</li>
                <li>RSS feed polling</li>
                <li>Google Sheet triggers</li>
                <li>Cleanup of old logs and temporary data</li>
              </ul>
              <Paragraph>
                <strong>Recommendation:</strong> Add the following line to your wp-config.php file:
              </Paragraph>
              <pre style={{ background: '#f6f8fa', padding: 10, borderRadius: 4 }}>
                {`define('DISABLE_WP_CRON', false);`}
              </pre>
              <Paragraph>
                If you're using a system cron job instead, make sure it's properly configured to call wp-cron.php regularly.
              </Paragraph>
            </div>
          }
          type="error"
          showIcon
        />
      );
    }
    
    // Cron performance issue alert
    if (requirements.cron.enabled && (requirements.cron.overdue > 5 || requirements.cron.maxOverdueTime > 60)) {
      cronAlerts.push(
        <Alert
          key="cron-performance"
          message="Cron Performance Issues Detected"
          description={
            <div>
              <Paragraph>
                Your WordPress cron appears to be running, but there are signs it may not be processing jobs efficiently.
              </Paragraph>
              <Paragraph>
                <strong>Recommendation:</strong> For best performance, consider setting up a system cron job instead of relying on WordPress default cron:
              </Paragraph>
              <ol>
                <li>Disable WordPress cron by adding to wp-config.php: <code>define('DISABLE_WP_CRON', true);</code></li>
                <li>Set up a system cron job to run every 5 minutes with this command:</li>
              </ol>
              <pre style={{ background: '#f6f8fa', padding: 10, borderRadius: 4 }}>
                {`*/5 * * * * wget -q -O /dev/null ${window.location.origin}/wp-cron.php?doing_wp_cron >/dev/null 2>&1`}
              </pre>
            </div>
          }
          type="warning"
          showIcon
        />
      );
    }
    
    return (
      <Card size="small" title="WordPress Cron Status" style={{ marginTop: 16 }}>
        <List size="small">
          <RequirementItem
            item={{
              title: "Cron Enabled",
              value: requirements.cron.enabled ? 'Yes' : 'No',
              status: requirements.cron.enabled ? 'success' : 'error',
              message: requirements.cron.enabled 
                ? "WordPress cron is enabled and working properly."
                : "WordPress cron is disabled. Many features of AI Workflow Automation require cron to function properly."
            }}
          />
          
          {requirements.cron.overdue !== undefined && (
            <RequirementItem
              item={{
                title: "Overdue Cron Jobs",
                value: `${requirements.cron.overdue} job(s)`,
                status: requirements.cron.overdue > 5 ? 'warning' : 'success',
                message: requirements.cron.overdue > 5 
                  ? "Multiple overdue cron jobs might indicate an issue with WordPress background processing."
                  : "Cron jobs are processing normally."
              }}
            />
          )}
          
          {requirements.cron.maxOverdueTime !== undefined && (
            <RequirementItem
              item={{
                title: "Longest Overdue Time",
                value: `${requirements.cron.maxOverdueTime} minutes`,
                status: requirements.cron.maxOverdueTime > 60 ? 'warning' : 'success',
                message: requirements.cron.maxOverdueTime > 60 
                  ? "Some cron jobs are significantly delayed. Consider setting up a system cron for better reliability."
                  : "Cron jobs are executing within an acceptable timeframe."
              }}
            />
          )}
        </List>
        
        {cronAlerts.length > 0 && (
          <Collapse 
            style={{ marginTop: 16 }} 
            bordered={false}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
          >
            <Panel header={<Text strong>Show Cron Recommendations</Text>} key="1">
              <Space direction="vertical" style={{ width: '100%' }}>
                {cronAlerts}
              </Space>
            </Panel>
          </Collapse>
        )}
      </Card>
    );
  };

  return (
    <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          message="System Requirements"
          description={statusMessages[overallStatus]}
          type={overallStatus}
          showIcon
        />
        
        <Card size="small" title="Environment Check" style={{ marginTop: 16 }}>
          <List
            size="small"
            dataSource={requirements.items}
            renderItem={item => (
              <RequirementItem item={item} />
            )}
          />
        </Card>

        {renderCronSection()}
        
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            type="default" 
            icon={<ReloadOutlined />} 
            onClick={fetchSystemRequirements}
          >
            Refresh System Status
          </Button>
        </div>
      </Space>
    </div>
  );
};

export default SystemRequirements;